import { useMemo, useState } from 'react';

import { fmt } from '@dcc-erp/shared';
import type { PettyCashFloatListRow } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { diffColor, todayIso } from './pc-helpers';
import { useCreatePettyCashCount } from '../../lib/queries/petty-cash';

// ============================================================
// Cash-count modal (PCC) — count the physical cash on hand, preview the
// over/short vs the float's currentBalance, then file the count. It enters the
// PCC approval chain (1 approver, FINANCE_MANAGER); the over/short JV posts on
// final approval. expectedBalance = currentBalance (server-derived); the live
// preview here mirrors that basis.
// ============================================================

interface PCCountModalProps {
  companyId: string | undefined;
  liveFloats: readonly PettyCashFloatListRow[];
  initialFloatId?: string | undefined;
  onClose: () => void;
}

export function PCCountModal({
  companyId,
  liveFloats,
  initialFloatId,
  onClose,
}: PCCountModalProps) {
  const activeFloats = useMemo(() => liveFloats.filter((f) => f.status === 'ACTIVE'), [liveFloats]);
  const [floatId, setFloatId] = useState<string>(initialFloatId ?? activeFloats[0]?.id ?? '');
  const [countDate, setCountDate] = useState<string>(todayIso());
  const [countedText, setCountedText] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const { mutate: createCount, isPending, error } = useCreatePettyCashCount(companyId);

  const selected = activeFloats.find((f) => f.id === floatId);
  const expected = selected ? Number(selected.currentBalance) : 0;
  // Live over/short preview = counted − expected (matches the server's basis).
  const diff = countedText.trim() ? Number(countedText) - expected : 0;

  function handleSubmit() {
    if (!floatId || !countedText.trim()) return;
    const countedBalance = Number(countedText);
    if (!Number.isFinite(countedBalance) || countedBalance < 0) return;
    createCount(
      {
        data: {
          floatId,
          countDate,
          countedBalance,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        },
        idempotencyKey: crypto.randomUUID(),
      },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="pc-count-modal"
        style={{
          background: 'white',
          borderRadius: 12,
          width: 560,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              นับเงินสด (Cash Count)
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              นับเงินสดจริงในมือ ระบบจะเทียบกับยอดในบัญชี แล้วเข้าสายอนุมัติ
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              Float สาขา
            </label>
            <select
              className="select"
              value={floatId}
              onChange={(e) => setFloatId(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
              disabled={activeFloats.length === 0}
            >
              {activeFloats.length === 0 && <option value="">— ไม่มี float ที่ active —</option>}
              {activeFloats.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.branchName} · คงเหลือในบัญชี {fmt(Number(f.currentBalance))} บาท
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                วันที่นับ
              </label>
              <input
                className="input"
                type="date"
                value={countDate}
                onChange={(e) => setCountDate(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                นับได้จริง (บาท)
              </label>
              <input
                className="input tnum"
                data-testid="pc-count-counted"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={countedText}
                onChange={(e) => setCountedText(e.target.value)}
                style={{
                  width: '100%',
                  height: 38,
                  fontSize: 16,
                  textAlign: 'right',
                  fontWeight: 600,
                }}
              />
            </div>
          </div>

          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              หมายเหตุ / เหตุผล (ถ้ามีผลต่าง)
            </label>
            <input
              className="input"
              placeholder="เช่น ทอนเงินผิด / เศษสตางค์"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>

          <div
            style={{
              padding: '12px 14px',
              background: '#fafafa',
              borderRadius: 8,
              border: '1px solid var(--border)',
              fontSize: 14,
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span className="muted">ยอดในบัญชี (expected)</span>
              <span className="tnum">{fmt(expected)} บาท</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span className="muted">ผลต่าง (นับได้ − บัญชี)</span>
              <span
                className="tnum"
                data-testid="pc-count-diff"
                style={{ fontWeight: 600, color: diffColor(diff) }}
              >
                {diff > 0 ? '+' : ''}
                {fmt(diff)} บาท {diff > 0 ? '(เกิน)' : diff < 0 ? '(ขาด)' : '(พอดี)'}
              </span>
            </div>
          </div>

          {error && (
            <div
              style={{
                padding: '10px 12px',
                background: 'var(--negative-bg)',
                borderRadius: 8,
                border: '1px solid var(--negative)',
                color: 'var(--negative)',
                fontSize: 14,
              }}
            >
              {error.message}
            </div>
          )}
        </div>

        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose} disabled={isPending}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            data-testid="pc-count-submit"
            onClick={handleSubmit}
            disabled={isPending || !floatId || !countedText.trim()}
          >
            <Icon name="check" size={14} /> {isPending ? 'กำลังบันทึก…' : 'บันทึกการนับ'}
          </button>
        </div>
      </div>
    </div>
  );
}

export type { PCCountModalProps };
